/**
 * ============================================================================
 * PayoutResultModal - SUCCESS/ERROR MODAL FOR PAYOUT REQUESTS
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. TWO COMPLETELY DIFFERENT STATES
 *    - Success: Green checkmark, celebration, show amount/reference
 *    - Error: Red X (or orange clock for rate limit), show error message
 *    - Determined by result.success boolean
 *    - Never mix the two states
 *
 * 2. RATE LIMITING SPECIAL CASE
 *    - Backend returns 429 status
 *    - We detect with result.isRateLimit
 *    - Show countdown timer with result.retryAt
 *    - Orange color scheme (not red) - it's temporary
 *    - Help user understand when they can retry
 *
 * 3. NO BROWSER ALERT() EVER
 *    - This modal replaces alert()
 *    - Professional UI for all outcomes
 *    - User-friendly error messages
 *    - Clear call-to-action buttons
 *
 * 4. ACCESSIBLE MODAL
 *    - Escape key should close
 *    - Focus on close button
 *    - Clear heading hierarchy
 *    - Screen reader friendly
 *
 * 5. PARENT HANDLES CACHE INVALIDATION
 *    - This modal only shows result
 *    - usePayoutRequest hook invalidates cache
 *    - When modal closes, parent has fresh data
 *    - Clean separation of concerns
 *
 * ============================================================================
 */

import React from 'react';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatCurrency } from '../../../utils/revenueFormatters';
import type { PayoutRequestResult } from '../../../types/revenue';
import Modal from '../../ui/Modal';

interface PayoutResultModalProps {
  isOpen: boolean;
  result: PayoutRequestResult;
  onClose: () => void;
}

/**
 * Modal showing payout request result (success or error)
 */
export function PayoutResultModal({ isOpen, result, onClose }: PayoutResultModalProps) {
  // Extract amount - try both locations (top-level and data.amount)
  const amount = result.amount || result.data?.amount || 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" animate={false}>
        {result.success ? (
          // ============================================================
          // SUCCESS STATE
          // ============================================================
          <div className="p-8 text-center">
            {/* Success Icon */}
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payout Requested!</h2>
            <p className="text-gray-600 mb-6">Your money is on the way</p>

            {/* Amount Details */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">Amount</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(amount)}
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Estimated Arrival</span>
                  <span className="font-semibold text-gray-900">
                    {result.estimated_arrival || result.data?.estimated_arrival || 'Within 24 hours'}
                  </span>
                </div>

                {(result.reference || result.data?.transfer_reference) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Reference</span>
                    <span className="font-mono text-xs text-gray-900">
                      {result.reference || result.data?.transfer_reference}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Info */}
            <p className="text-sm text-gray-500 mb-6">
              💰 Money will arrive in your registered bank account within 24 hours
            </p>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition"
            >
              Got it, thanks!
            </button>
          </div>
        ) : (
          // ============================================================
          // ERROR STATE
          // ============================================================
          <div className="p-8 text-center">
            {/* Error Icon */}
            <div
              className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                result.isRateLimit ? 'bg-orange-100' : 'bg-red-100'
              }`}
            >
              {result.isRateLimit ? (
                <Clock className="w-10 h-10 text-orange-600" />
              ) : (
                <XCircle className="w-10 h-10 text-red-600" />
              )}
            </div>

            {/* Error Heading */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {result.isRateLimit ? 'Too Many Attempts' : 'Payout Failed'}
            </h2>
            <p className="text-gray-600 mb-6">
              {result.isRateLimit
                ? 'Please wait before trying again'
                : "We couldn't process your request"}
            </p>

            {/* Error Message */}
            <div
              className={`border rounded-xl p-4 mb-6 ${
                result.isRateLimit
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <p
                className={`text-sm ${
                  result.isRateLimit ? 'text-orange-800' : 'text-red-800'
                }`}
              >
                {result.error || 'An unknown error occurred'}
              </p>

              {/* Rate Limit Details */}
              {result.isRateLimit && (
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <p className="text-xs text-orange-700 mb-2">Rate Limit Info:</p>
                  <div className="text-sm text-orange-900 space-y-1">
                    <p>
                      <strong>Allowed:</strong> 10 requests per minute
                    </p>
                    <p>
                      <strong>Try again at:</strong> {result.retryAt}
                    </p>
                    <p>
                      <strong>Wait time:</strong> ~{result.retryAfter} seconds
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="space-y-3">
              <button
                onClick={onClose}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition"
              >
                Close
              </button>

              {/* Support Contact */}
              <p className="text-xs text-gray-500">
                Need help? Contact support at info@spottsapp.com
              </p>
            </div>
          </div>
        )}
    </Modal>
  );
}
